import React, { useState, useMemo } from 'react';
import { ServiceType } from '../../../../services/ConfigurationService/types/serviceType';
import { useUpsertServiceMutation } from '../../../../services/ConfigurationService';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { EditServiceDialog } from '../dialogs/EditServiceDialog';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface ServiceGridRow {
  id: string;
  code: string;
  description: string;
}

export function ServiceGrid({ services, centerId, onServiceSaved }: {
  services: ServiceType[];
  centerId: string;
  onServiceSaved: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [editService, setEditService] = useState<ServiceType | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [upsertService] = useUpsertServiceMutation();

  // Filter services based on search term
  const filteredServices = useMemo(() => {
    if (!searchTerm.trim()) {
      return services;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return services.filter(service => 
      (service.code?.toLowerCase().includes(searchLower)) ||
      (service.description?.toLowerCase().includes(searchLower))
    );
  }, [services, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle row selection for editing
  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data) {
      const selectedRow = args.data as ServiceGridRow;
      const service = services.find(s => s.id === selectedRow.id);
      if (service) {
        setEditService(service);
        setEditDialogOpen(true);
      }
    }
  };

  const handleDialogSave = async (service: ServiceType) => {
    const id = `${centerId}-${service.code}`;
    await upsertService({ ...service, centerId, id });
    setEditDialogOpen(false);
    setEditService(null);
    onServiceSaved();
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditService(null);
  };

  if (services.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by service code, description..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No services found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No services available for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredServices.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by service code, description..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No services match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by service code, description..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredServices}
        height={500}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
          <ColumnsDirective>
            <ColumnDirective field="code" headerText="Code" width="120" textAlign="Left" />
            <ColumnDirective field="description" headerText="Description" width="200" textAlign="Left" />
          </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>

      {/* Edit Dialog */}
      {editDialogOpen && editService && (
        <EditServiceDialog
          service={editService}
          onCancel={handleEditDialogClose}
          onSuccess={onServiceSaved}
        />
      )}
    </Box>
  );
}
